import { roundMoney } from "@/domain/money/round-money";
import { BILLING_MONEY_EPSILON } from "@/domain/billing/order-billing.constants";
import type { BillingDisplayLine, ResolvedOrderDiscount } from "@/domain/billing/order-billing.types";

export type DiscountResolutionInput = {
  hasOrderBillSummary: boolean;
  summaryTotalDiscount: number;
  itemizedDiscounts: ReadonlyArray<{ name?: string; amount: number }>;
};

const DEFAULT_DISCOUNT_LABEL = "Discount";

function isPositiveAmount(amount: number): boolean {
  return amount > BILLING_MONEY_EPSILON;
}

function sumItemizedAmounts(
  itemizedDiscounts: ReadonlyArray<{ name?: string; amount: number }>
): number {
  return roundMoney(
    itemizedDiscounts.reduce((acc, line) => acc + Math.max(0, Number(line.amount) || 0), 0)
  );
}

function toDiscountDisplayLines(
  itemizedDiscounts: ReadonlyArray<{ name?: string; amount: number }>
): BillingDisplayLine[] {
  return itemizedDiscounts
    .filter((line) => isPositiveAmount(Number(line.amount) || 0))
    .map((line) => ({
      kind: "discount" as const,
      label: line.name?.trim() || DEFAULT_DISCOUNT_LABEL,
      amount: roundMoney(Math.max(0, Number(line.amount) || 0)),
    }));
}

function summaryDiscountLine(amount: number): BillingDisplayLine {
  return {
    kind: "discount",
    label: DEFAULT_DISCOUNT_LABEL,
    amount: roundMoney(amount),
  };
}

/**
 * Resolves a single canonical discount amount for an order.
 *
 * Strategy:
 * 1. When `order_bill_summary` exists and `total_discount` is positive → use summary only.
 * 2. Otherwise → sum positive `orderDiscountList` lines (itemized).
 * 3. Never add summary and itemized amounts together (avoids double counting).
 */
export function resolveOrderDiscount(input: DiscountResolutionInput): ResolvedOrderDiscount {
  const summaryAmount = roundMoney(Math.max(0, Number(input.summaryTotalDiscount) || 0));
  const itemizedLines = toDiscountDisplayLines(input.itemizedDiscounts);
  const itemizedTotal = sumItemizedAmounts(input.itemizedDiscounts);

  if (input.hasOrderBillSummary && isPositiveAmount(summaryAmount)) {
    return {
      totalAmount: summaryAmount,
      source: "summary",
      displayLines: [summaryDiscountLine(summaryAmount)],
    };
  }

  if (isPositiveAmount(itemizedTotal)) {
    return {
      totalAmount: itemizedTotal,
      source: "itemized",
      displayLines: itemizedLines,
    };
  }

  return {
    totalAmount: 0,
    source: "none",
    displayLines: [],
  };
}
